import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { QueryClient } from 'react-query';
import getSelectedProfile from '@api/profiles';
import { getLandingPage } from '@api/landingPage';
import getFeatures from '@api/features';
import { getPreDefinedMetricsMeta, getUserDefinedMetricsMeta } from '@api/meta';
import { searchFilter } from '@api/filters';
import { getSelectedKPI, getSPIThresholds } from '@api/SPI';
import { getDashboardTypes } from '@api/dashboards';
import { getDimensions } from '@api/dimensionContainer';
import useMetricsStore from '@globalStore/metricsStore';
import useKpiStore from '@globalStore/kpiStore';
import useDimensionsStore from '@globalStore/dimensionsStore';
import { mergeObjectsWithSameKey } from '@utils/common';
import {
  updateProfiles,
  updateInterval,
  updateFeatures,
  updateDashboardType,
  setAiAlertsSensitivity,
} from '@store/common/actions';
import { updateDefaultLandingPage, setCustomerId } from '@store/defaultLandingPage/actions';
import { initTabs } from '@store/dashboards/tabs/actions';
import { setDefaultFilters } from '@store/filterContainer/actions';
import { fetchingSuccess } from '@store/common/dimensionList/actions';
import {
  DATE_SHORTCUTS,
  DASHBOARD_TYPE,
  ALL_TRAFFIC,
  DEFAULT_GLOBAL_ACCOUNT,
  isAdModule,
  isEcoServerModule,
  isOdpModule,
} from '@utils/constants';
import { calcShortcutInterval } from '@utils';
import execFor from '@utils/platform/execFor';
import { getSensitivity } from '@api/aiAlerts';
import { initOdp } from './initOdp';
import { initEco } from './initEco';

const setMomentDefaultTimezone = (timezone) => moment.tz.setDefault(timezone);

const clickDocument = () => window.top?.postMessage({ type: 'click inside iframe' }, '*');

const initOdpContext = execFor.odp(() => {
  try {
    const matches = window.location.pathname.match(/odp\/dashboard\/(.*?)\/.*$/);
    if (matches && matches.length > 1) {
      window.conviva.contextId = matches[1];
    }
  } catch (err) {
    // pass
  }
});

const initContextForEcoServer = () => {
  try {
    //eslint-disable-next-line
    const matches = window.location.pathname.match(/eco-server\/dashboard\/trends\/(\w+)/);
    if (matches && matches.length > 1) {
      const context = matches[1];
      window.conviva.context = context;
    }
  } catch (err) {
    // pass
  }
};

const initProfiles = async () => {
  const resp = await getSelectedProfile();
  return resp.data;
};

const initDefaultLandingPage = async () => {
  const resp = await getLandingPage({});
  return resp.data && resp.data.configuration;
};

const initFeatures = async () => {
  const resp = await getFeatures({});
  return resp.data;
};

const initMetricsPredefinedMeta = async (queryClient) => {
  const context = window.conviva.context;
  const { data, error } = await queryClient.fetchQuery(
    'pre-defined-metrics-meta',
    () => getPreDefinedMetricsMeta({ context: context }),
    {
      retry: true,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );
  if (error) {
    throw error;
  }
  return data;
};

const initAiAlertsSensitivity = async (queryClient) => {
  if (isOdpModule || isEcoServerModule) {
    return [];
  }
  const { data, error } = await queryClient.fetchQuery(
    'ai-alerts-sensitivity',
    () => getSensitivity({}),
    {
      retry: true,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );
  if (error) {
    throw error;
  }
  return data;
};

const initUserDefinedMetrics = async (queryClient) => {
  const context = window.conviva.context;
  const { data, error } = await queryClient.fetchQuery(
    'user-defined-metrics-meta',
    () => getUserDefinedMetricsMeta({ context: context }),
    {
      retry: true,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );
  if (error) {
    throw error;
  }
  return data;
};

// load default filter (only All Traffic for now)
// required for NOC
const initDefaultFilters = async () => {
  const resp = await searchFilter({ name: ALL_TRAFFIC, isDefault: 1, isModifiable: 0 });
  return resp.data;
};

// prefetch and cache metric thresholds
const initUserKpi = async (queryClient) => {
  if (isOdpModule || isAdModule || isEcoServerModule) {
    return true;
  }

  await queryClient.prefetchQuery('selected-kpi', () => getSelectedKPI({}), { retry: false });
  return true;
};

const initGetKpiList = async (queryClient) => {
  if (isOdpModule || isAdModule || isEcoServerModule) {
    return [];
  }

  const { data } = await queryClient.fetchQuery('kpi-list', () => getSPIThresholds({}), {
    retry: false,
  });
  return data.kpiThresholds || [];
};

const initDashboardTypes = async () => {
  const resp = await getDashboardTypes();
  const keys = Object.keys(resp.data);

  /* Update DASHBOARD_TYPE constant properties */
  keys.forEach((key) => {
    DASHBOARD_TYPE[key] = resp.data[key];
  });
  return resp.data;
};

const initDimensions = async () => {
  const context = window.conviva.context;
  const resp = await getDimensions({ context: context });
  return resp.data;
};

export default (queryClient: QueryClient) => {
  initOdpContext();
  initContextForEcoServer();

  // interval should not be consumed here as it casues app level re-renders
  // initialize should only happen once and in the first run interval is always empty
  // also interval changes don't re-run the effect hook so consuming it make less sense
  // const interval = useSelector(intervalSelector);

  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    document.addEventListener('click', clickDocument);
    return () => document.removeEventListener('click', clickDocument);
  }, []);

  useEffect(() => {
    Promise.all([
      initProfiles(),
      initFeatures(),
      initDashboardTypes(),
      initDefaultFilters(),
      initDefaultLandingPage(),
      initGetKpiList(queryClient),
      initDimensions(),
      initUserDefinedMetrics(queryClient),
      initMetricsPredefinedMeta(queryClient),
      initAiAlertsSensitivity(queryClient),
      initUserKpi(queryClient),
    ])
      .then(
        ([
          profilesData,
          featuresData,
          dashboardTypes,
          defaultFilters,
          defaultLandingPage,
          kpiList,
          dimensionList,
          userDefinedMetrics,
          preDefinedMetrics,
          aiAlertsSensitivity,
          prefetchKpi,
        ]) => {
          const selectedProfile = profilesData.find((p) => p.is_selected);
          const { setDimensionsMeta } = useDimensionsStore.getState();
          const {
            setPreDefinedMetricsMeta,
            setUserDefinedMetricsMeta,
            setCombinedMetricsMeta,
            setCombinedMetricsKey,
          } = useMetricsStore.getState();
          setDimensionsMeta(dimensionList);
          setPreDefinedMetricsMeta(preDefinedMetrics);
          setUserDefinedMetricsMeta(userDefinedMetrics);
          const metricsMeta = mergeObjectsWithSameKey(preDefinedMetrics, userDefinedMetrics);
          const activeMetricsKeys = Object.keys(metricsMeta).reduce((acc: any, key: any) => {
            const metricsByCategory = metricsMeta[key];
            const activeMetrics = Object.values(metricsByCategory)
              .filter((metric: any) => metric.active)
              .map((metric: any) => metric.name);
            return {
              ...acc,
              [key]: activeMetrics,
            };
          }, {});
          setCombinedMetricsMeta(metricsMeta);
          setCombinedMetricsKey(activeMetricsKeys);
          // identifier for gloabl account (c3.Conviva-Global)
          // global account has special behaviour in multiple sections
          // this flag helps do it without polluting props and params
          window.isGlobalAccount = selectedProfile.customer_id === DEFAULT_GLOBAL_ACCOUNT;

          setMomentDefaultTimezone(selectedProfile.tzName);
          dispatch(setCustomerId(selectedProfile.customer_id));
          dispatch(updateDefaultLandingPage(defaultLandingPage));
          dispatch(updateProfiles(profilesData));
          dispatch(initTabs());
          dispatch(
            updateFeatures({
              ...featuresData,
              //userDefinedMetrics: userDefinedMetrics.map(m => m.name),
            }),
          );
          dispatch(setAiAlertsSensitivity(aiAlertsSensitivity));
          //dispatch(updateUserDefinedMetrics(userDefinedMetrics));

          /* TODO: Adding dashboards types object in common store for future reference,
           * For now, it's directly updated in {DASHBOARD_TYPE} constant and referred.
           * @utils/constants/DASHBOARD_TYPE
           *
           * Use below id in the editor to find the occurences of it's relative references.
           * Editor search key: dashboard_types_UUE93ID
           */
          dispatch(updateDashboardType(dashboardTypes));
          dispatch(setDefaultFilters([defaultFilters]));
          dispatch(fetchingSuccess(dimensionList));

          initOdp({ userDefinedMetrics, dispatch, dimensionList });
          initEco({ userDefinedMetrics, dispatch, dimensionList });

          /**
           * GLOBAL ACC requires default data loading time to be last24hours
           * This is a border case to be added for PII request
           */
          if (selectedProfile.customer_id == DEFAULT_GLOBAL_ACCOUNT) {
            const [from, to] = calcShortcutInterval(DATE_SHORTCUTS.past24Hours);
            dispatch(
              updateInterval(from.format(), to.format(), DATE_SHORTCUTS.past24Hours, 'trends'),
            );
          } else {
            const [from, to] = calcShortcutInterval(DATE_SHORTCUTS.last6Hours);
            dispatch(
              updateInterval(from.format(), to.format(), DATE_SHORTCUTS.last6Hours, 'trends'),
            );
          }

          const { setKPIs } = useKpiStore.getState();
          setKPIs(kpiList);
          setInitialized(true);
        },
      )
      .catch((err) => {
        setError(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setInitialized, dispatch]);

  return [initialized, error];
};
